import { motion } from 'framer-motion';
import { Phone, Mail, MapPin, Clock, MessageCircle } from 'lucide-react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import LeadForm from '@/components/LeadForm';

const Contact = () => {
  return (
    <div className="min-h-screen bg-dark" data-testid="contact-page">
      <Header />

      {/* Hero */}
      <section className="relative min-h-[450px] flex items-center justify-center overflow-hidden mt-32 bg-gradient-to-br from-dark via-dark-light to-dark">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0 bg-gradient-to-r from-rose-gold/20 to-transparent"></div>
        </div>
        <div className="relative z-10 text-center px-6 max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <p className="text-rose-gold text-sm mb-6 uppercase tracking-widest font-semibold">Get In Touch</p>
            <h1 className="font-heading text-6xl md:text-8xl font-bold text-white mb-6 leading-tight">
              Contact <span className="text-rose-gold">Us</span>
            </h1>
            <p className="text-xl text-white/70 max-w-2xl mx-auto">
              Let's help you find your dream property
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-24 px-6 md:px-12 lg:px-24 bg-dark">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 max-w-7xl mx-auto">
          {/* Contact Info */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            className="space-y-8"
          >
            <div>
              <h2 className="font-heading text-4xl md:text-5xl font-bold text-white mb-6">
                Let's Start a <span className="text-rose-gold">Conversation</span>
              </h2>
              <p className="text-white/70 text-lg leading-relaxed">
                Whether you're buying your first home, looking for an investment property, or need expert real estate guidance, our team is here to help you every step of the way.
              </p>
            </div>

            <div className="space-y-6">
              <a href="tel:+917020970161" className="flex items-start gap-4 p-6 glass-effect rounded-2xl border border-dark-lighter hover:border-rose-gold/50 transition-all group">
                <div className="p-3 bg-rose-gold/20 rounded-full">
                  <Phone className="text-rose-gold group-hover:scale-110 transition-transform" size={24} />
                </div>
                <div>
                  <p className="text-white/50 text-sm font-semibold mb-2 uppercase tracking-wider">Phone</p>
                  <p className="text-white text-xl font-bold hover:text-rose-gold transition-colors">
                    +91 70209 70161
                  </p>
                  <p className="text-white/50 text-sm mt-1">Mon-Sat: 9 AM - 7 PM</p>
                </div>
              </a>

              <a href="https://wa.me/917020970161" target="_blank" rel="noopener noreferrer" className="flex items-start gap-4 p-6 glass-effect rounded-2xl border border-dark-lighter hover:border-green-500/50 transition-all group">
                <div className="p-3 bg-green-500/20 rounded-full">
                  <MessageCircle className="text-green-500 group-hover:scale-110 transition-transform" size={24} />
                </div>
                <div>
                  <p className="text-white/50 text-sm font-semibold mb-2 uppercase tracking-wider">WhatsApp</p>
                  <p className="text-white text-xl font-bold hover:text-green-500 transition-colors">
                    Message Us Instantly
                  </p>
                  <p className="text-white/50 text-sm mt-1">Quick response guaranteed</p>
                </div>
              </a>

              <a href="mailto:info@propvanta.co.in" className="flex items-start gap-4 p-6 glass-effect rounded-2xl border border-dark-lighter hover:border-rose-gold/50 transition-all group">
                <div className="p-3 bg-rose-gold/20 rounded-full">
                  <Mail className="text-rose-gold group-hover:scale-110 transition-transform" size={24} />
                </div>
                <div>
                  <p className="text-white/50 text-sm font-semibold mb-2 uppercase tracking-wider">Email</p>
                  <p className="text-white text-xl font-bold hover:text-rose-gold transition-colors">
                    info@propvanta.co.in
                  </p>
                  <p className="text-white/50 text-sm mt-1">We'll respond within 24 hours</p>
                </div>
              </a>

              <div className="flex items-start gap-4 p-6 glass-effect rounded-2xl border border-dark-lighter">
                <div className="p-3 bg-rose-gold/20 rounded-full">
                  <MapPin className="text-rose-gold" size={24} />
                </div>
                <div>
                  <p className="text-white/50 text-sm font-semibold mb-2 uppercase tracking-wider">Office</p>
                  <p className="text-white text-xl font-bold">Mumbai, Maharashtra</p>
                  <p className="text-white/50 text-sm mt-1">Visit us by appointment</p>
                </div>
              </div>

              <div className="flex items-start gap-4 p-6 glass-effect rounded-2xl border border-dark-lighter">
                <div className="p-3 bg-rose-gold/20 rounded-full">
                  <Clock className="text-rose-gold" size={24} />
                </div>
                <div>
                  <p className="text-white/50 text-sm font-semibold mb-2 uppercase tracking-wider">Working Hours</p>
                  <p className="text-white font-semibold">Monday - Saturday</p>
                  <p className="text-white/60 text-sm">9:00 AM - 7:00 PM</p>
                  <p className="text-white/50 text-sm mt-2">Sunday: By Appointment</p>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Contact Form */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
          >
            <LeadForm title="Send Us a Message" />
          </motion.div>
        </div>
      </section>

      {/* Map Section */}
      <section className="py-20 px-6 md:px-12 lg:px-24 bg-dark-light">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl font-bold text-white mb-4">
              Find <span className="text-rose-gold">Us</span>
            </h2>
            <p className="text-white/60">
              Visit our office for personalized consultation
            </p>
          </div>
          <div className="h-[400px] rounded-2xl overflow-hidden border-2 border-dark-lighter">
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d241317.11609823277!2d72.71637095!3d19.082197799999998!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3be7c6306644edc1%3A0x5da4ed8f8d648c69!2sMumbai%2C%20Maharashtra!5e0!3m2!1sen!2sin!4v1234567890123"
              width="100%"
              height="100%"
              style={{ border: 0, filter: 'invert(90%) hue-rotate(180deg)' }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            ></iframe>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Contact;
